import {useCallback, useContext, useEffect, useState} from 'react'
import { notifyError } from '../../../../utils/notifications'
import {Col, Flex, Form, Modal, Row, Upload, Typography} from 'antd'
import {UploadOutlined} from '@ant-design/icons'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {
  SET_SELECTED_VISUAL_AID_DATA,
  SHOW_HIDE_VISUAL_AID_FORM,
  UPDATE_CHANGE_VISUAL_AID_DATA,
  VisualAidContext,
} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'
import useDebouncedValue from '../../../../utils/common_function'
import {INVALID_VISUAL_AID_FILE} from '../../../../constants/common'

const VisualAidFormModal = ({getVisualAidList}) => {
  const intl = useIntl()
  const {vehicle} = useParams()
  const [state, dispatch] = useContext(VisualAidContext)
  const [, navDispatch] = useContext(NavContext)
  const [searchFailed, setSearchFailed] = useState(false)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const [visualAidTypeOptions, setVisualAidTypeOptions] = useState([])
  const [optionCodes, setOptionCodes] = useState([])
  const [searchSalesCode, setSearchSalesCode] = useState('')
  const [showOptionCodeLoader, setShowOptionCodeLoader] = useState(false)
  const debouncedSearchSalesCode = useDebouncedValue(searchSalesCode, 1000)

  const fetchTypes = useCallback(async () => {
    let res = await getRequest(
      `/api/visual_aid/visual-aid-type/?vehicle=${vehicle}`
    )
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'visualAid.typesLoadError' }))
      return
    }
    setVisualAidTypeOptions(res?.data || [])
  }, [vehicle])

  const fetchOptionCodes = useCallback(async (search = '') => {
    setShowOptionCodeLoader(true)
    let res = await getRequest(
      `/api/option_codes/options/?vehicle=${vehicle}&search=${search}`
    )
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setSearchFailed(true)
      setOptionCodes([])
      setShowOptionCodeLoader(false)
      return
    }
    setSearchFailed(false)
    setOptionCodes(res?.data || [])
    setShowOptionCodeLoader(false)
  }, [vehicle])

  useEffect(() => {
    fetchTypes()
    fetchOptionCodes('')
  }, [fetchTypes, fetchOptionCodes])

  useEffect(() => {
    fetchOptionCodes(searchSalesCode)
  }, [debouncedSearchSalesCode])

  const closeFormPopup = () => {
    dispatch({type: SHOW_HIDE_VISUAL_AID_FORM, data: false})
    form.resetFields()
  }

  const saveVisualAid = async (values) => {
    setSaving(true)

    const selected = state?.selectedObject || {}
    const formData = new FormData()
    formData.append('vehicle', vehicle)

    formData.append('name', values.name || '')
    formData.append('code', values.code || '')
    formData.append(
      'aid_type',
      values.aid_type?.value || values.aid_type || ''
    )
    formData.append(
      'visual_aid_file_name',
      values.visual_aid_file_name || ''
    )

    // Handle sales_codes (multi-select array)
    if (Array.isArray(values.sales_codes)) {
      values.sales_codes.forEach((sc) => {
        formData.append('sales_codes', sc?.value || sc)
      })
    }

    // Handle file upload
    // The field is `valuePropName="fileList"` with a getValueFromEvent that
    // returns e.fileList, so the form value is the array itself - not an
    // event object with a .fileList on it.
    if (values.visual_aid_file?.[0]?.originFileObj) {
      formData.append(
        'visual_aid_file',
        values.visual_aid_file[0].originFileObj
      )
    }

    let res = null
    if (selected?.id) {
      res = await putRequest(
        `/api/visual_aid/${selected.id}/?vehicle=${vehicle}`,
        formData
      )
    } else {
      res = await postRequest(`/api/visual_aid/`, formData)
    }

    setSaving(false)

    if (res?.status) {
      if (selected?.id) {
        dispatch({
          type: UPDATE_CHANGE_VISUAL_AID_DATA,
          data: selected,
        })
      }
      // getVisualAidList() opens with a 'loader' dispatch carrying
      // notification: {}, and that action overwrites `notification` - so a
      // success toast dispatched before it is wiped in the same React batch.
      // This is the trap in the reducer documented for in-function sequences,
      // one stack frame down: the wiping dispatch is inside the callee, so it
      // has to be awaited before the toast is dispatched.
      await getVisualAidList()
      closeFormPopup()
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({id: 'visualAid.saveSuccess'}),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          // A rejected SVG is a fixable mistake, not a failure - saying which
          // it was matters, since the two used to be indistinguishable.
          msg: intl.formatMessage({
            id:
              res?.code === INVALID_VISUAL_AID_FILE
                ? 'visualAid.invalidFile'
                : 'visualAid.saveError',
          }),
        },
      })
    }
  }

  // Initialize form when opening
  useEffect(() => {
    if (!state.showVisualAidFormPopup) return
    const obj = state?.selectedObject || {}
    form.setFieldsValue({
      name: obj.name || '',
      code: obj.code || '',
      aid_type: obj.aid_type
        ? {value: obj.aid_type, label: obj.aid_type}
        : null,
      sales_codes: obj.sales_codes_data || [],
      visual_aid_file_name: obj.visual_aid_file_name || '',
      visual_aid_file: obj.visual_aid_file
        ? [{name: obj.visual_aid_file_name, status: 'done'}]
        : [],
    })
  }, [state.showVisualAidFormPopup])

  return (
    <Modal
      open={state.showVisualAidFormPopup}
      title={intl.formatMessage({
        id: state?.selectedObject?.id
          ? 'visualAid.editVisualAid'
          : 'visualAid.newVisualAid',
      })}
      width="60%"
      className="visual-aid-form-modal"
      centered
      footer={null}
      closable
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form form={form} layout="vertical" onFinish={saveVisualAid}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <InputText
                name="name"
                label={intl.formatMessage({id: 'visualAid.name'})}
                isRequired
              />
            </Col>

            <Col span={12}>
              <InputText
                name="code"
                label={intl.formatMessage({id: 'visualAid.code'})}
                isRequired
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="aid_type"
                label={intl.formatMessage({id: 'visualAid.aidType'})}
                options={visualAidTypeOptions}
                isRequired
              />
            </Col>

            <Col span={12}>
              <SelectInput
                name="sales_codes"
                label={intl.formatMessage({id: 'visualAid.salesCode'})}
                options={optionCodes}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                isMulti
                isRequired={false}
                showLoader={showOptionCodeLoader}
                onInputChange={(value) => {
                  setSearchSalesCode(value || '')
                }}
                maxTagCount={3}
              />
            </Col>

            <Col span={12}>
              <Form.Item
                name="visual_aid_file"
                label={intl.formatMessage({
                  id: 'visualAid.visualAidFile',
                })}
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList || [])}
                style={{marginBottom: 0}}
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  accept=".svg,.png,.jpg,.jpeg"
                >
                  <Button icon={<UploadOutlined />}>
                    {intl.formatMessage({id: 'visualAid.upload'})}
                  </Button>
                </Upload>
              </Form.Item>
            </Col>

            <Col span={12}>
              <InputText
                name="visual_aid_file_name"
                label={intl.formatMessage({
                  id: 'visualAid.visualAidFileName',
                })}
                isRequired
              />
            </Col>

            <Col span={24}>
              <Flex justify="end">
                <Button htmlType="submit" type="primary" loading={saving}>
                  {intl.formatMessage({id: 'visualAid.save'})}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default VisualAidFormModal
